import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { getTranslation } from "common";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import { useTranslations } from "hooks";
import {
  buildProcessExportRows,
  downloadProcessExportCsv,
  downloadProcessExportPdf,
  getProcessExportStandardColumns,
  getProcessExportUserDataColumns,
  getProcessExportUserDataKeys,
  printProcessExport,
  ProcessExportColumn,
  ProcessExportFormat,
} from "utils/processExport";
import { StartedProcess } from "./ProcessItem";

interface ProcessExportButtonProps {
  processes: StartedProcess[];
  history: boolean;
  title: string;
  onError?: (message: string) => void;
}

const FORMAT_LABELS: Record<ProcessExportFormat, string> = {
  csv: "CSV",
  pdf: "PDF",
  print: "Print",
};

const FORMAT_ICONS: Record<ProcessExportFormat, React.ReactNode> = {
  csv: <DescriptionOutlinedIcon fontSize="small" />,
  pdf: <PictureAsPdfOutlinedIcon fontSize="small" />,
  print: <PrintOutlinedIcon fontSize="small" />,
};

const STANDARD_COLUMN_TRANSLATION_KEYS: Record<string, string> = {
  process: "generic.model_name.caption",
  processName: "generic.process.name",
  description: "generic.description",
  task: "generic.task",
  status: "generic.status",
  startDate: "generic.filter.data.start.process",
  endDate: "generic.filter.data.end.process",
  processDuration: "generic.process.duration",
  waitingTime: "generic.waiting_time.caption",
  workingTime: "generic.working_time.caption",
  priority: "generic.priorities.caption",
  labels: "generic.labels.caption",
  groups: "groups.name",
  roles: "roles.name",
  participants: "generic.participants",
};

const FIELD_SECTION_SX = {
  backgroundColor: "#eeeeee",
  borderColor: "divider",
  borderRadius: 2,
  p: 1.5,
};

const ProcessExportButton: React.FC<ProcessExportButtonProps> = ({
  processes,
  history,
  title,
  onError,
}) => {
  const translations = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFormat, setSelectedFormat] = useState<ProcessExportFormat | null>(null);
  const [selectedColumnIds, setSelectedColumnIds] = useState<string[]>([]);

  const translateWithFallback = (key: string, fallback: string) => {
    const translated = getTranslation(translations, key);
    return translated === key ? fallback : translated;
  };

  const standardColumns = useMemo(
    () =>
      getProcessExportStandardColumns(history).map((column) => ({
        ...column,
        label: STANDARD_COLUMN_TRANSLATION_KEYS[column.id]
          ? translateWithFallback(STANDARD_COLUMN_TRANSLATION_KEYS[column.id], column.label)
          : column.label,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, translations]
  );
  const userDataColumns = useMemo(
    () => getProcessExportUserDataColumns(getProcessExportUserDataKeys(processes)),
    [processes]
  );
  const allColumns = useMemo(
    () => [...standardColumns, ...userDataColumns],
    [standardColumns, userDataColumns]
  );
  const selectedColumns = useMemo(
    () => allColumns.filter((column) => selectedColumnIds.includes(column.id)),
    [allColumns, selectedColumnIds]
  );

  useEffect(() => {
    setSelectedColumnIds(allColumns.map((column) => column.id));
  }, [allColumns]);

  const closeMenu = () => setAnchorEl(null);

  const handleFormatSelect = (format: ProcessExportFormat) => {
    setSelectedFormat(format);
    closeMenu();
  };

  const closeDialog = () => setSelectedFormat(null);

  const toggleColumn = (column: ProcessExportColumn) => {
    setSelectedColumnIds((current) =>
      current.includes(column.id)
        ? current.filter((id) => id !== column.id)
        : [...current, column.id]
    );
  };

  const toggleSection = (columns: ProcessExportColumn[], checked: boolean) => {
    const ids = columns.map((column) => column.id);
    setSelectedColumnIds((current) =>
      checked
        ? Array.from(new Set([...current, ...ids]))
        : current.filter((id) => !ids.includes(id))
    );
  };

  const executeExport = () => {
    if (!selectedFormat) return;

    if (!processes.length) {
      onError?.("No filtered processes to export");
      return;
    }

    if (!selectedColumns.length) {
      onError?.("Select at least one field to export");
      return;
    }

    const rows = buildProcessExportRows(processes, selectedColumns);
    if (selectedFormat === "csv") {
      downloadProcessExportCsv(rows, selectedColumns, title);
    }
    if (selectedFormat === "pdf") {
      downloadProcessExportPdf(rows, selectedColumns, title);
    }
    if (selectedFormat === "print") {
      const opened = printProcessExport(rows, selectedColumns, title);
      if (!opened) {
        onError?.("Unable to open print window");
        return;
      }
    }
    closeDialog();
  };

  const renderColumnList = (columns: ProcessExportColumn[]) => (
    <FormGroup
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: 2,
      }}
    >
      {columns.map((column) => (
        <FormControlLabel
          key={column.id}
          sx={{
            mr: 0,
            minWidth: 0,
          }}
          slotProps={{
            typography: {
              sx: {
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            },
          }}
          control={
            <Checkbox
              id={`process-export-field-${column.id}`}
              checked={selectedColumnIds.includes(column.id)}
              onChange={() => toggleColumn(column)}
            />
          }
          label={column.label}
        />
      ))}
    </FormGroup>
  );

  const renderSelectAllRadio = (columns: ProcessExportColumn[], checked: boolean) => (
    <FormControlLabel
      control={
        <Radio
          checked={checked}
          onClick={() => toggleSection(columns, !checked)}
        />
      }
      label={getTranslation(translations, "generic.select.all")}
    />
  );

  const standardChecked = standardColumns.every((column) => selectedColumnIds.includes(column.id));
  const userDataChecked = userDataColumns.length > 0 && userDataColumns.every((column) => selectedColumnIds.includes(column.id));

  return (
    <React.Fragment>
      <FlowentiButton
        id="process-export-button"
        variant="outlined"
        sx={{ mr: 1, whiteSpace: "nowrap" }}
        startIcon={<FileDownloadOutlinedIcon color="primary" />}
        disabled={!processes.length}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {getTranslation(translations, "generic.export")}
      </FlowentiButton>
      <Menu
        id="process-export-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        {(Object.keys(FORMAT_LABELS) as ProcessExportFormat[]).map((format) => (
          <MenuItem
            id={`process-export-format-${format}`}
            key={format}
            onClick={() => handleFormatSelect(format)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {FORMAT_ICONS[format]}
              <span>{FORMAT_LABELS[format]}</span>
            </Stack>
          </MenuItem>
        ))}
      </Menu>
      <Dialog
        open={Boolean(selectedFormat)}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {getTranslation(translations, "generic.export")} {selectedFormat ? FORMAT_LABELS[selectedFormat] : ""}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box sx={FIELD_SECTION_SX}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{getTranslation(translations, "generic.standard.fields")}</Typography>
                {renderSelectAllRadio(standardColumns, standardChecked)}
              </Box>
              {renderColumnList(standardColumns)}
            </Box>
            <Divider />
            <Box sx={FIELD_SECTION_SX}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle2">{getTranslation(translations, "generic.userdata")}</Typography>
                {userDataColumns.length > 0 && (
                  renderSelectAllRadio(userDataColumns, userDataChecked)
                )}
              </Box>
              {userDataColumns.length > 0 ? (
                renderColumnList(userDataColumns)
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No userdata available for the filtered processes.
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <FlowentiButton variant="outlined" sx={{ py: 1 }} onClick={closeDialog}>
            Cancel
          </FlowentiButton>
          <FlowentiButton
            id="process-export-confirm"
            variant="contained"
            sx={{ py: 1 }}
            disabled={!selectedColumns.length || !processes.length}
            onClick={executeExport}
          >
            {getTranslation(translations, "generic.export")}
          </FlowentiButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default ProcessExportButton;
